import { CnpjInput } from '@/_components/inputs/cnpj-input'
import { ApiQueryError, mutations } from '@/features/_lib/react-query'
import type { TCreateCompanyInputDTO } from '@doutorsim/shared/companies'
import { CreateCompanyInputDTOSchema } from '@doutorsim/shared/companies'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import type { z } from 'zod'
import {
  Button,
  DialogClose,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '../../../_components/ui'
import { Text, Title } from '../../_components/ui/typography'

interface CreateCompanyDialogProps {
  setIsDialogOpen: (isOpen: boolean) => void
}

const CreateCompanyFormSchema = CreateCompanyInputDTOSchema.omit({
  franchiseId: true,
})

type TCreateCompanyForm = z.infer<typeof CreateCompanyFormSchema>

export const CreateCompanyDialog = ({ setIsDialogOpen }: CreateCompanyDialogProps) => {
  const queryClient = useQueryClient()
  const staffCreateCompany = useMutation(mutations.staffCompanies.create(queryClient))

  const form = useForm<TCreateCompanyForm>({
    resolver: zodResolver(CreateCompanyFormSchema),
    shouldUnregister: true,
    defaultValues: {
      name: '',
      legalName: '',
      cnpj: '',
    },
  })

  const onSubmit = (formData: TCreateCompanyForm) => {
    const createCompanyInput: TCreateCompanyInputDTO = { ...formData, franchiseId: null }

    staffCreateCompany.mutate(createCompanyInput, {
      onSuccess: () => {
        toast.success('Cadastro efetuado!', {
          description: 'A instituição foi criada com sucesso!',
        })
        setIsDialogOpen(false)
      },
      onError: (error) => {
        toast.error('Erro ao cadastrar instituição.', {
          description:
            error instanceof ApiQueryError
              ? (error.apiError.detail ?? error.message)
              : 'Erro inesperado',
        })
      },
    })
  }

  return (
    <Form {...form}>
      <div className="flex flex-col items-center justify-center gap-1">
        <Title>Cadastro de instituição</Title>
        <Text>Insira as informações abaixo</Text>
      </div>
      <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-semibold">Nome Fantasia</FormLabel>
              <FormControl>
                <Input placeholder="Digite o nome fantasia da empresa" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="legalName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-semibold">Razão Social</FormLabel>
              <FormControl>
                <Input placeholder="Digite a razão social da empresa" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="cnpj"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-semibold">CNPJ</FormLabel>
              <FormControl>
                <CnpjInput placeholder="Digite o CNPJ da instituição" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="mt-8 flex w-full justify-center gap-3">
          <DialogClose asChild>
            <Button
              variant="ghost"
              className="font-bold hover:bg-transparent hover:text-brand-grayscale-12"
              type="reset"
            >
              Cancelar
            </Button>
          </DialogClose>
          <Button
            type="submit"
            disabled={staffCreateCompany.isPending}
            className="w-[142px] font-bold"
          >
            {staffCreateCompany.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Cadastrar
          </Button>
        </div>
      </form>
    </Form>
  )
}
